// @ts-ignore package has no types
import HtmlParser from "react-html-parser";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useT, T } from "@transifex/react";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { CircularProgress } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { Button } from "storybook/components/button";
import { Typography } from "storybook/components/typography";
import { Notification } from "storybook/components/notification";
import { NAVIGATOR_HOSTNAME, SPECIALIST_HOSTNAME } from "env-vars";
import { zodResolver } from "storybook/utils/form";
import { RiskProfileProvider, useRiskProfile } from "context/risk-profile";
import { useAdminContent, useFeatureFlags } from "context/admin-content";
import { RiskReportProvider, useRiskReport } from "context/risk-report";
import { PatientInterestProvider } from "context/patient-interest";
import { Loading } from "components/loading";
import { useTakeAction } from "hooks/use-take-action";
import { GeneticTestingAccordion } from "./accordions/genetic-testing";
import { BreastMriAccordion } from "./accordions/breast-mri-default";
import { LungCtAccordion } from "./accordions/lung-ct-default";
import { DeclineModal } from "./decline-modal";
import { ScreeningOption } from "./payload-helper";
import {
  Container,
  RemainderText,
  BannerContainer,
  ButtonsContainer,
  CalendarContainer,
  AccordionContainer,
  AccordorionsSection,
} from "./styles";
import { buildPayloads } from "./payload-helper";
import { DeclineMode, takeActionSchema, TakeActionType, WeekDay } from "./schema";
import { PreferredAppointmentAvailability } from "./preferred-appointment-availability";

const options = {
  shouldValidate: true,
};

const defaultAvailability = { enabled: false, am: false, pm: false };

function TakeActionPage() {
  const t = useT();
  const riskProfile = useRiskProfile();
  const featureFlags = useFeatureFlags();
  const riskResult = riskProfile?.riskProfile;
  const {
    appointmentsAvailability,
    showBreastMRI,
    showLungCT,
    refetchPatient,
    showGeneticTesting,
    breastMRICheckboxEnabled,
    lungCTCheckboxEnabled,
    geneticTestingCheckboxEnabled,
    isLoading: useTakeActionLoading,
  } = useTakeAction({ riskResult });
  const history = useHistory();
  const adminData = useAdminContent();
  const [error, setError] = useState<boolean>(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [declinedMode, setDeclinedMode] = useState<
    "breast-mri" | "genetic-testing" | "lung-ct"
  >();
  const { reportHtml, isLoading: riskReportLoading } = useRiskReport();

  const isLoading =
    !adminData ||
    adminData.isLoading ||
    riskProfile?.isLoading ||
    riskReportLoading ||
    useTakeActionLoading;

  const form = useForm<TakeActionType>({
    resolver: zodResolver(takeActionSchema),
    defaultValues: {
      screenings: {
        geneticTesting: false,
        breastMRI: false,
        lungCT: false,
      },
      preferredAppointmentAvailability: {
        anytime: false,
        monday: defaultAvailability,
        tuesday: defaultAvailability,
        wednesday: defaultAvailability,
        thursday: defaultAvailability,
        friday: defaultAvailability,
      },
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { isValid },
  } = form;

  const handleDeclineClose = useCallback(
    (mode: DeclineMode) => {
      if (mode) {
        if (mode === "genetic-testing") {
          setValue("screenings.geneticTesting", true, options);
        } else if (mode === "breast-mri") {
          setValue("screenings.breastMRI", true, options);
        } else if (mode === "lung-ct") {
          setValue("screenings.lungCT", true, options);
        }
      }

      setDeclinedMode(undefined);
    },
    [setValue]
  );

  const handleDeclineSuccessful = useCallback(
    (mode: DeclineMode) => {
      if (mode) {
        if (mode === "genetic-testing") {
          setValue("screenings.geneticTesting", false, options);
        } else if (mode === "breast-mri") {
          setValue("screenings.breastMRI", false, options);
        } else if (mode === "lung-ct") {
          setValue("screenings.lungCT", false, options);
        }
      } else {
        refetchPatient();
      }

      setDeclinedMode(undefined);
    },
    [refetchPatient, setValue]
  );

  useEffect(() => {
    if (!appointmentsAvailability) return;

    if (appointmentsAvailability.anytime) {
      setValue("preferredAppointmentAvailability.anytime", true, options);
      return;
    }

    const setDayAvailability = (
      availability?: ("AM" | "PM")[],
      day?: WeekDay
    ) => {
      if (!day || !availability) return;
      setValue(
        `preferredAppointmentAvailability.${day}.enabled`,
        availability.length > 0,
        options
      );
      setValue(
        `preferredAppointmentAvailability.${day}.am`,
        availability.includes("AM"),
        options
      );
      setValue(
        `preferredAppointmentAvailability.${day}.pm`,
        availability.includes("PM"),
        options
      );
    };

    setValue("preferredAppointmentAvailability.anytime", false, options);

    (
      ["monday", "tuesday", "wednesday", "thursday", "friday"] as WeekDay[]
    ).forEach((day) => {
      setDayAvailability(appointmentsAvailability[day], day);
    });
  }, [appointmentsAvailability, setValue]);

  useEffect(() => {
    setValue("screenings.geneticTesting", geneticTestingCheckboxEnabled);
  }, [geneticTestingCheckboxEnabled, setValue]);

  useEffect(() => {
    setValue("screenings.breastMRI", breastMRICheckboxEnabled);
  }, [breastMRICheckboxEnabled, setValue]);

  useEffect(() => {
    setValue("screenings.lungCT", lungCTCheckboxEnabled);
  }, [lungCTCheckboxEnabled, setValue]);

  const { nextStepsGeneticTesting, nextStepsBreastMri, nextStepsLungCT } =
    useMemo(() => {
      if (!reportHtml) {
        return {
          nextStepsGeneticTesting: null,
          nextStepsBreastMri: null,
          nextStepsLungCT: null,
        };
      }

      const root = document.createElement("div");
      root.innerHTML = reportHtml;

      const testing = root.querySelector("#next-steps-genetic-testing");
      const breastMRI = root.querySelector("#next-steps-breast-mri");
      const lungCT = root.querySelector("#next-steps-lung-ct");

      return {
        nextStepsGeneticTesting: testing ? HtmlParser(testing.innerHTML) : null,
        nextStepsBreastMri: breastMRI ? HtmlParser(breastMRI.innerHTML) : null,
        nextStepsLungCT: lungCT ? HtmlParser(lungCT.innerHTML) : null,
      };
    }, [reportHtml]);

  // We read the checkboxes from the form
  const isTestingSelected = getValues("screenings.geneticTesting");
  const isMRISelected = getValues("screenings.breastMRI");
  const isLungCTSelected = getValues("screenings.lungCT");

  const selectedScreenings = useMemo(() => {
    const selectedScreenings: ScreeningOption[] = [];

    if (isTestingSelected) {
      selectedScreenings.push(ScreeningOption.GENETIC_TESTING);
    }
    if (isMRISelected) {
      selectedScreenings.push(ScreeningOption.BREAST_MRI);
    }
    if (isLungCTSelected) {
      selectedScreenings.push(ScreeningOption.LUNG_CT);
    }

    return selectedScreenings;
  }, [isLungCTSelected, isMRISelected, isTestingSelected]);

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("explore-options", "true");
  const completeUrl = `/complete?${searchParams.toString()}`;

  useEffect(() => {
    const { unsubscribe } = watch((value, { name, type }) => {
      if (type === "change") {
        switch (name) {
          case "screenings.geneticTesting":
            if (!value?.screenings?.geneticTesting) {
              setDeclinedMode("genetic-testing");
            }
            break;
          case "screenings.breastMRI":
            if (!value?.screenings?.breastMRI) {
              setDeclinedMode("breast-mri");
            }
            break;
          case "screenings.lungCT":
            if (!value?.screenings?.lungCT) {
              setDeclinedMode("lung-ct");
            }
            break;
          default:
            break;
        }
      }
    });
    return () => unsubscribe();
  }, [watch]);

  const handleFormSubmit = useCallback(
    async (data: TakeActionType) => {
      setError(false);
      setSubmitting(true);

      try {
        const idToken = localStorage.getItem("id_token");
        const patientId = localStorage.getItem("ciq_id") || "";
        const auth0UserEmail = localStorage.getItem("ciq_email") || "";

        const currentTime = Math.floor(new Date().getTime());

        const { navigatorPayload, specialistPayload } = buildPayloads({
          email: auth0UserEmail,
          currentTime,
          screenings: selectedScreenings,
          preferredAppointmentAvailability:
            data.preferredAppointmentAvailability,
          specialistCompatiblePayload: !!featureFlags.removeAccessToNavigatorApp
        });

        const fetchPromises: Promise<Response>[] = [];

        if (navigatorPayload) {
          fetchPromises.push(
            fetch(`${NAVIGATOR_HOSTNAME}/api/v1/people/${patientId}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify(navigatorPayload),
            })
          );
        }

        if (specialistPayload) {
          fetchPromises.push(
            fetch(`${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify(specialistPayload),
            })
          );
        }

        if (fetchPromises.length === 0) {
          setSubmitting(false);
          history.push(completeUrl);
          return;
        }

        const responses = await Promise.all(fetchPromises);

        if (responses.some((r) => !r.ok)) {
          setError(true);
          setSubmitting(false);
          return;
        }

        history.push(completeUrl);
      } catch (err) {
        console.error("Error submitting form:", err);
        setError(true);
      } finally {
        setSubmitting(false);
      }
    },
    [history, selectedScreenings, completeUrl, featureFlags.removeAccessToNavigatorApp]
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container>
      <FormProvider {...form}>
        <form
          aria-label="eligibility-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(handleFormSubmit)();
          }}
        >
          <Typography theme="default" variant="body1" color="black">
            <T
              _str="Your suggested next step(s) are pre-selected below. To confirm your interest in scheduling, {reminder}"
              reminder={
                <RemainderText>
                  <T _str="don't forget to hit the SUBMIT button below." />
                </RemainderText>
              }
            />
          </Typography>
          {error && (
            <BannerContainer>
              <Notification
                theme="default"
                variant="warning"
                fullWidth={true}
                title={t("Something went wrong, please try again")}
              />
            </BannerContainer>
          )}
          <AccordorionsSection>
            {showGeneticTesting && (
              <AccordionContainer>
                <GeneticTestingAccordion content={nextStepsGeneticTesting} />
              </AccordionContainer>
            )}
            {showBreastMRI && (
              <AccordionContainer>
                <BreastMriAccordion content={nextStepsBreastMri} />
              </AccordionContainer>
            )}
            {showLungCT && (
              <AccordionContainer>
                <LungCtAccordion content={nextStepsLungCT} />
              </AccordionContainer>
            )}
          </AccordorionsSection>
          <CalendarContainer>
            <Typography
              theme="default"
              variant="h5"
              component="h2"
              color="black"
            >
              <T _str="What's the best time for us to call you? (Optional)" />
            </Typography>
            <Typography theme="default" variant="body1" color="black">
              <T _str="Please indicate the best times for your care team to get in touch to help you schedule your appointment(s). If you're not sure, you can skip this step." />
            </Typography>
            <br />
            <Typography theme="default" variant="body1" color="black">
              <T _str="(Select all that apply)" />
            </Typography>
            <PreferredAppointmentAvailability />
          </CalendarContainer>
          <ButtonsContainer>
            {isSubmitting ? (
              <CircularProgress />
            ) : (
              <>
                <Button
                  size="large"
                  color="default"
                  variant="text"
                  theme="default"
                  href={completeUrl}
                  disabled={isSubmitting}
                  startIcon={<ArrowBackIcon />}
                >
                  <T _str="Back to my Results" />
                </Button>
                <Button
                  size="large"
                  type="submit"
                  color="default"
                  theme="default"
                  variant="contained"
                  disabled={
                    !(
                      (isTestingSelected ||
                        isMRISelected ||
                        isLungCTSelected) &&
                      isValid
                    ) || isSubmitting
                  }
                >
                  <T _str="Submit" />
                </Button>
              </>
            )}
          </ButtonsContainer>
        </form>
      </FormProvider>
      {declinedMode && (
        <DeclineModal
          mode={declinedMode}
          onClose={handleDeclineClose}
          onSuccessfulDecline={handleDeclineSuccessful}
        />
      )}
    </Container>
  );
}

export function TakeAction() {
  return (
    <PatientInterestProvider>
      <RiskProfileProvider>
        <RiskReportProvider>
          <TakeActionPage />
        </RiskReportProvider>
      </RiskProfileProvider>
    </PatientInterestProvider>
  );
}
